import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Plus,
  RefreshCw,
  Users,
  Warehouse as WarehouseIcon,
} from "lucide-react";
import { doc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { useAuth } from "../../auth/AuthContext";
import { db, storage } from "../../configs/firebase";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import WarehouseCard, { WarehouseItem } from "./WarehouseCard";

const rawApiBase =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://api.pickdengu.mw");

const normalizedApiBase = String(rawApiBase || "").replace(/\/$/, "");
const cardsBorderRadii ="8px";
const API_BASE_URL = normalizedApiBase.endsWith("/api/v1")
  ? normalizedApiBase
  : `${normalizedApiBase}/api/v1`;

type WarehouseManagerProps = {
  warehouses: WarehouseItem[];
  onWarehousesUpdated: (warehouses: WarehouseItem[]) => void;
};

type WarehouseForm = {
  name: string;
  description: string;
  address: string;
  lat: string;
  lng: string;
  photoURL: string;
  isActive: boolean;
  isDefaultDispatch: boolean;
  supportsPickup: boolean;
  supportsDelivery: boolean;
};

type WarehouseDriverAssignment = {
  id?: string;
  ownerUserId?: string;
  warehouseId?: string;
  warehouseName?: string;
  warehouseAddress?: string;
  driverId?: string;
  driverName?: string;
  driverPhone?: string;
  isPrimary?: boolean;
  isBackup?: boolean;
  active?: boolean;
  orderTypes?: string[];
  [key: string]: any;
};

type WarehouseDriverRow = {
  assignment?: WarehouseDriverAssignment;
  driver?: Record<string, any> | null;
  driverResolved?: boolean;
};

type DriverDisplay = {
  id: string;
  name: string;
  phoneNumber: string;
  whatsappNumber: string;
  vehicleType: string;
  status: string;
  currentShiftStatus: string;
  currentJobsCount: number;
  maxConcurrentJobs: number;
  serviceCategories: string[];
  driverResolved: boolean;
  assignment: WarehouseDriverAssignment;
};

const emptyWarehouseForm: WarehouseForm = {
  name: "",
  description: "",
  address: "",
  lat: "",
  lng: "",
  photoURL: "",
  isActive: true,
  isDefaultDispatch: false,
  supportsPickup: true,
  supportsDelivery: true,
};

const getDriverId = (driver: any) =>
  String(
    driver?.id ||
      driver?._id ||
      driver?.driverId ||
      driver?.userId ||
      driver?.firebaseUid ||
      driver?.authUid ||
      "",
  ).trim();

const normalizeServiceCategory = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getDriverAvailability = (driver: any) => {
  const status = String(driver?.status || "")
    .trim()
    .toLowerCase();
  const shift = String(driver?.currentShiftStatus || "")
    .trim()
    .toLowerCase();

  const currentJobs = Math.max(0, Number(driver?.currentJobsCount || 0));
  const maxConcurrentJobs = Math.max(1, Number(driver?.maxConcurrentJobs || 2));

  const serviceCategories = Array.isArray(driver?.serviceCategories)
    ? driver.serviceCategories.map(normalizeServiceCategory)
    : [];

  const online = status === "active";
  const onShift = shift === "on_shift";
  const hasCapacity = currentJobs < maxConcurrentJobs;
  const supportsDelivery =
    serviceCategories.length === 0 ||
    serviceCategories.includes("delivery") ||
    serviceCategories.includes("product_order");

  const eligible = online && onShift && hasCapacity && supportsDelivery;

  let reason = "Ready for jobs";

  if (!online) {
    reason = "Driver is offline";
  } else if (!onShift) {
    reason = "Driver is off shift";
  } else if (!hasCapacity) {
    reason = `At capacity (${currentJobs}/${maxConcurrentJobs})`;
  } else if (!supportsDelivery) {
    reason = "Delivery service is not enabled";
  }

  return {
    online,
    eligible,
    reason,
    currentJobs,
    maxConcurrentJobs,
  };
};

const getDriverDisplay = (row: WarehouseDriverRow): DriverDisplay => {
  const assignment = row?.assignment || {};
  const driver = row?.driver || {};
  const driverId = String(
    assignment.driverId || getDriverId(driver) || "",
  ).trim();

  return {
    id: getDriverId(driver) || driverId,
    name:
      String(driver?.name || assignment.driverName || "").trim() ||
      (driverId ? `Driver ${driverId}` : "Unknown driver"),
    phoneNumber: String(
      driver?.phoneNumber || assignment.driverPhone || "",
    ).trim(),
    whatsappNumber: String(
      driver?.whatsappNumber || assignment.driverPhone || "",
    ).trim(),
    vehicleType: String(driver?.vehicleType || "").trim(),
    status: String(driver?.status || "").trim(),
    currentShiftStatus: String(driver?.currentShiftStatus || "").trim(),
    currentJobsCount: Number(driver?.currentJobsCount || 0),
    maxConcurrentJobs: Number(driver?.maxConcurrentJobs || 2),
    serviceCategories: Array.isArray(driver?.serviceCategories)
      ? driver.serviceCategories.map((value: unknown) => String(value || ""))
      : [],
    driverResolved: row?.driverResolved ?? Boolean(row?.driver),
    assignment,
  };
};

const createWarehouseId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `warehouse-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

const WarehouseManager: React.FC<WarehouseManagerProps> = ({
  warehouses,
  onWarehousesUpdated,
}) => {
  const { user } = useAuth();
  const { theme } = useCustomTheme();
  const toast = useToast();
  const modal = useDisclosure();

  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const [editingWarehouse, setEditingWarehouse] =
    useState<WarehouseItem | null>(null);
  const [form, setForm] = useState<WarehouseForm>(emptyWarehouseForm);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingWarehouseId, setDeletingWarehouseId] = useState("");

  const [drivers, setDrivers] = useState<any[]>([]);
  const [driverPoolLoading, setDriverPoolLoading] = useState(false);
  const [assignmentRows, setAssignmentRows] = useState<
    Record<string, WarehouseDriverRow[]>
  >({});
  const [assignmentLoading, setAssignmentLoading] = useState<
    Record<string, boolean>
  >({});
  const [expandedWarehouseIds, setExpandedWarehouseIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [busyDriverKey, setBusyDriverKey] = useState("");

  const ownerUserId = String(user?.uid || "").trim();

  const warehouseIdsKey = useMemo(
    () =>
      warehouses
        .map((warehouse) => String(warehouse.id || "").trim())
        .filter(Boolean)
        .sort()
        .join("|"),
    [warehouses],
  );

  const persistWarehouses = async (nextWarehouses: WarehouseItem[]) => {
    if (!ownerUserId) {
      throw new Error("You must be signed in to manage warehouses.");
    }

    await setDoc(
      doc(db, "users", ownerUserId),
      {
        warehouses: nextWarehouses,
      },
      { merge: true },
    );

    onWarehousesUpdated(nextWarehouses);
  };

  const openAddWarehouse = () => {
    setEditingWarehouse(null);
    setForm(emptyWarehouseForm);
    setPhotoFile(null);
    modal.onOpen();
  };

  const openEditWarehouse = (warehouse: WarehouseItem) => {
    setEditingWarehouse(warehouse);
    setForm({
      name: warehouse.name || "",
      description: warehouse.description || "",
      address: warehouse.address || "",
      lat: Number.isFinite(Number(warehouse.lat)) ? String(warehouse.lat) : "",
      lng: Number.isFinite(Number(warehouse.lng)) ? String(warehouse.lng) : "",
      photoURL: warehouse.photoURL || "",
      isActive: warehouse.isActive !== false,
      isDefaultDispatch: warehouse.isDefaultDispatch === true,
      supportsPickup: warehouse.supportsPickup !== false,
      supportsDelivery: warehouse.supportsDelivery !== false,
    });
    setPhotoFile(null);
    modal.onOpen();
  };

  const uploadWarehousePhoto = async (warehouseId: string) => {
    if (!photoFile || !ownerUserId) {
      return form.photoURL;
    }

    const safeName = photoFile.name.replace(/[^a-zA-Z0-9._-]/g, "-");
    const photoRef = ref(
      storage,
      `warehouse-images/${ownerUserId}/${warehouseId}/${Date.now()}-${safeName}`,
    );

    await uploadBytes(photoRef, photoFile);
    return getDownloadURL(photoRef);
  };

  const saveWarehouse = async () => {
    const name = form.name.trim();
    const address = form.address.trim();
    const lat = Number(form.lat);
    const lng = Number(form.lng);

    if (!name || !address) {
      toast({
        title: "Name and address are required",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast({
        title: "Valid latitude and longitude are required",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);

    try {
      const warehouseId = editingWarehouse?.id || createWarehouseId();
      const photoURL = await uploadWarehousePhoto(warehouseId);
      const now = Date.now();

      const nextWarehouse: WarehouseItem = {
        id: warehouseId,
        name,
        description: form.description.trim(),
        address,
        lat,
        lng,
        photoURL: String(photoURL || "").trim(),
        createdAt: editingWarehouse?.createdAt || now,
        updatedAt: now,
        isActive: form.isActive,
        isDefaultDispatch: form.isDefaultDispatch,
        supportsPickup: form.supportsPickup,
        supportsDelivery: form.supportsDelivery,
      };

      let nextWarehouses: WarehouseItem[];

      if (editingWarehouse) {
        nextWarehouses = warehouses.map((warehouse) =>
          warehouse.id === editingWarehouse.id ? nextWarehouse : warehouse,
        );
      } else {
        nextWarehouses = [nextWarehouse, ...warehouses];
      }

      if (nextWarehouse.isDefaultDispatch) {
        nextWarehouses = nextWarehouses.map((warehouse) => ({
          ...warehouse,
          isDefaultDispatch: warehouse.id === nextWarehouse.id,
        }));
      }

      await persistWarehouses(nextWarehouses);

      toast({
        title: editingWarehouse ? "Warehouse updated" : "Warehouse added",
        status: "success",
        duration: 2200,
        isClosable: true,
      });

      modal.onClose();
      setEditingWarehouse(null);
      setForm(emptyWarehouseForm);
      setPhotoFile(null);
    } catch (error) {
      console.error("Failed to save warehouse:", error);
      toast({
        title: "Failed to save warehouse",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteWarehouse = async (warehouse: WarehouseItem) => {
    const confirmed = window.confirm(
      `Delete ${warehouse.name}? This removes the warehouse from your business profile.`,
    );

    if (!confirmed) return;

    setDeletingWarehouseId(warehouse.id);

    try {
      const nextWarehouses = warehouses.filter(
        (current) => current.id !== warehouse.id,
      );

      await persistWarehouses(nextWarehouses);

      setAssignmentRows((current) => {
        const next = { ...current };
        delete next[warehouse.id];
        return next;
      });

      toast({
        title: "Warehouse deleted",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to delete warehouse:", error);
      toast({
        title: "Failed to delete warehouse",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setDeletingWarehouseId("");
    }
  };

  const loadDriverPool = async (silent = false) => {
    if (!ownerUserId) return [];

    setDriverPoolLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/drivers`, {
        headers: { Accept: "application/json" },
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.message || payload?.error || "Drivers could not be loaded.",
        );
      }

      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      setDrivers(rows);
      return rows;
    } catch (error) {
      console.error("Failed to load drivers:", error);
      if (!silent) {
        toast({
          title: "Failed to load drivers",
          description:
            error instanceof Error ? error.message : "Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
      return [];
    } finally {
      setDriverPoolLoading(false);
    }
  };

  const loadWarehouseDrivers = async (
    warehouseId: string,
    options?: { silent?: boolean },
  ) => {
    const id = String(warehouseId || "").trim();
    if (!ownerUserId || !id) return [];

    setAssignmentLoading((current) => ({ ...current, [id]: true }));

    try {
      const params = new URLSearchParams({ ownerUserId });
      const response = await fetch(
        `${API_BASE_URL}/warehouses/${encodeURIComponent(id)}/drivers?${params.toString()}`,
        { headers: { Accept: "application/json" } },
      );
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            "Warehouse drivers could not be loaded.",
        );
      }

      const rows = Array.isArray(payload?.data) ? payload.data : [];
      setAssignmentRows((current) => ({ ...current, [id]: rows }));
      return rows;
    } catch (error) {
      console.error(`Failed to load drivers for warehouse ${id}:`, error);
      setAssignmentRows((current) => ({ ...current, [id]: [] }));

      if (!options?.silent) {
        toast({
          title: "Failed to load warehouse drivers",
          description:
            error instanceof Error ? error.message : "Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }

      return [];
    } finally {
      setAssignmentLoading((current) => ({ ...current, [id]: false }));
    }
  };

  const refreshWarehouseDriverData = async (silent = false) => {
    if (!ownerUserId) return;

    await loadDriverPool(silent);

    await Promise.all(
      warehouses
        .map((warehouse) => String(warehouse.id || "").trim())
        .filter(Boolean)
        .map((warehouseId) =>
          loadWarehouseDrivers(warehouseId, { silent: true }),
        ),
    );
  };

  useEffect(() => {
    if (!ownerUserId) {
      setDrivers([]);
      setAssignmentRows({});
      return;
    }

    void refreshWarehouseDriverData(true);
    // warehouseIdsKey intentionally refreshes when warehouse membership changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerUserId, warehouseIdsKey]);

  const toggleDrivers = (warehouseId: string) => {
    setExpandedWarehouseIds((current) => {
      const next = new Set(current);
      if (next.has(warehouseId)) next.delete(warehouseId);
      else next.add(warehouseId);
      return next;
    });
  };

  const assignDriver = async (warehouse: WarehouseItem, driver: any) => {
    const warehouseId = String(warehouse.id || "").trim();
    const driverId = getDriverId(driver);

    if (!ownerUserId || !warehouseId || !driverId) {
      toast({
        title: "Unable to assign driver",
        description: "Warehouse or driver information is missing.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const currentRows = assignmentRows[warehouseId] || [];
    const assignedIds = new Set(
      currentRows
        .map((row) =>
          String(row?.assignment?.driverId || row?.driver?.id || "").trim(),
        )
        .filter(Boolean),
    );

    if (assignedIds.has(driverId)) {
      toast({ title: "Driver already assigned", status: "info" });
      return;
    }

    const busyKey = `assign:${warehouseId}:${driverId}`;
    setBusyDriverKey(busyKey);

    try {
      const response = await fetch(
        `${API_BASE_URL}/warehouses/${encodeURIComponent(
          warehouseId,
        )}/drivers/${encodeURIComponent(driverId)}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ownerUserId,
            isPrimary: currentRows.length === 0,
            isBackup: currentRows.length > 0,
            orderTypes: ["delivery", "product_order"],
          }),
        },
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.message || payload?.error || "Driver could not be assigned.",
        );
      }

      await loadWarehouseDrivers(warehouseId, { silent: true });

      toast({
        title: "Driver assigned",
        description: `${driver?.name || "Driver"} was added to ${warehouse.name}.`,
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to assign driver:", error);
      toast({
        title: "Failed to assign driver",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3200,
        isClosable: true,
      });
    } finally {
      setBusyDriverKey("");
    }
  };

  const removeDriver = async (
    warehouse: WarehouseItem,
    row: WarehouseDriverRow,
  ) => {
    const warehouseId = String(warehouse.id || "").trim();
    const driverId = String(
      row?.assignment?.driverId || row?.driver?.id || "",
    ).trim();

    if (!ownerUserId || !warehouseId || !driverId) return;

    const busyKey = `remove:${warehouseId}:${driverId}`;
    setBusyDriverKey(busyKey);

    try {
      const params = new URLSearchParams({ ownerUserId });
      const response = await fetch(
        `${API_BASE_URL}/warehouses/${encodeURIComponent(
          warehouseId,
        )}/drivers/${encodeURIComponent(driverId)}?${params.toString()}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ownerUserId }),
        },
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.message || payload?.error || "Driver could not be removed.",
        );
      }

      setAssignmentRows((current) => ({
        ...current,
        [warehouseId]: (current[warehouseId] || []).filter((currentRow) => {
          const currentDriverId = String(
            currentRow?.assignment?.driverId || currentRow?.driver?.id || "",
          ).trim();
          return currentDriverId !== driverId;
        }),
      }));

      toast({
        title: "Driver removed",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to remove warehouse driver:", error);
      toast({
        title: "Failed to remove driver",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3200,
        isClosable: true,
      });
    } finally {
      setBusyDriverKey("");
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor={theme.border}
      borderRadius={cardsBorderRadii}
      bg={theme.surface}
      boxShadow={theme.shadowSm}
      overflow="visible"
      p={{ base: 3, md: 5 }}
    >
      <Flex
        justify="space-between"
        align={{ base: "stretch", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap={3}
      >
        <HStack spacing={2} minW={0}>
          <WarehouseIcon size={20} color={theme.text} />
          <Text fontSize="lg" fontWeight="800" color={theme.text}>
            Warehouses
          </Text>
          <Badge colorScheme="green" borderRadius={cardsBorderRadii} variant="subtle">
            {warehouses.length}
          </Badge>
        </HStack>

        <Button
          size="sm"
          leftIcon={<Plus size={15} />}
          onClick={openAddWarehouse}
          borderRadius={cardsBorderRadii}
          color={theme.textOnPrimary}
          bgImage={theme.gradients.primaryButton}
          _hover={{ bgImage: theme.gradients.primaryButtonHover }}
          alignSelf={{ base: "stretch", sm: "auto" }}
        >
          Add Warehouse
        </Button>
      </Flex>

      <Text mt={4} mb={4} fontSize="sm" color={theme.textMuted}>
        Manage warehouse branches, fulfillment hubs, pickup centers, or storage
        points.
      </Text>

      {warehouses.length === 0 ? (
        <Box
          py={8}
          px={4}
          border="1px dashed"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          bg={theme.surfaceAlt}
          textAlign="center"
        >
          <Text fontWeight="700" color={theme.text}>
            No warehouses yet
          </Text>
          <Text mt={1} fontSize="sm" color={theme.textMuted}>
            Add your first warehouse to configure pickup and delivery dispatch.
          </Text>
        </Box>
      ) : (
        <VStack spacing={3} align="stretch">
          {warehouses.map((warehouse) => {
            const warehouseId = String(warehouse.id || "").trim();
            const rows = assignmentRows[warehouseId] || [];
            const expanded = expandedWarehouseIds.has(warehouseId);
            const loading = Boolean(assignmentLoading[warehouseId]);
            const assignedIds = new Set(
              rows
                .map((row) =>
                  String(
                    row?.assignment?.driverId || row?.driver?.id || "",
                  ).trim(),
                )
                .filter(Boolean),
            );

            return (
              <WarehouseCard
                key={warehouseId}
                warehouse={warehouse}
                onEdit={openEditWarehouse}
                onDelete={deleteWarehouse}
                deleting={deletingWarehouseId === warehouseId}
              >
                <Box>
                  <Button
                    w="100%"
                    minH="44px"
                    h="auto"
                    px={{ base: 3, md: 4 }}
                    py={2.5}
                    borderRadius="0"
                    variant="ghost"
                    justifyContent="space-between"
                    onClick={() => toggleDrivers(warehouseId)}
                    _hover={{ bg: theme.hover }}
                    _active={{ bg: theme.surfaceAlt }}
                  >
                    <HStack spacing={2}>
                      <Users size={16} />
                      <Text fontSize="sm" fontWeight="800" color={theme.text}>
                        Drivers
                      </Text>
                      <Badge
                        colorScheme={rows.length ? "blue" : "gray"}
                        borderRadius={cardsBorderRadii}
                        variant="subtle"
                      >
                        {rows.length}
                      </Badge>
                    </HStack>

                    <ChevronDownIcon
                      boxSize={5}
                      color={theme.textMuted}
                      transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
                      transition="transform .18s ease"
                    />
                  </Button>

                  {expanded ? (
                    <Box
                      px={{ base: 3, md: 4 }}
                      pb={{ base: 3, md: 4 }}
                      borderTop="1px solid"
                      borderColor={theme.border}
                    >
                      <Flex
                        pt={3}
                        mb={3}
                        gap={2}
                        justify="space-between"
                        align={{ base: "stretch", sm: "center" }}
                        direction={{ base: "column", sm: "row" }}
                      >
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            color={theme.text}
                          >
                            Assigned drivers
                          </Text>
                          <Text fontSize="xs" color={theme.textMuted}>
                            Add or remove drivers for this warehouse.
                          </Text>
                        </Box>

                        <HStack spacing={2}>
                          <DriverPicker
                            drivers={drivers}
                            assignedDriverIds={assignedIds}
                            warehouseActive={warehouse.isActive !== false}
                            busy={Boolean(busyDriverKey)}
                            loading={driverPoolLoading}
                            theme={theme}
                            onAssign={(driver) =>
                              assignDriver(warehouse, driver)
                            }
                          />

                          <IconButton
                            aria-label="Refresh drivers"
                            icon={
                              loading ? (
                                <Spinner size="xs" />
                              ) : (
                                <RefreshCw size={14} />
                              )
                            }
                            size="sm"
                            variant="outline"
                            borderRadius={cardsBorderRadii}
                            borderColor={theme.border}
                            onClick={() =>
                              void loadWarehouseDrivers(warehouseId)
                            }
                            isDisabled={loading}
                          />
                        </HStack>
                      </Flex>

                      {loading ? (
                        <Flex py={5} align="center" justify="center">
                          <Spinner size="sm" />
                        </Flex>
                      ) : rows.length === 0 ? (
                        <Box
                          p={3}
                          borderRadius={cardsBorderRadii}
                          border="1px dashed"
                          borderColor={theme.border}
                          bg={theme.surfaceAlt}
                        >
                          <Text fontSize="sm" color={theme.textMuted}>
                            No drivers assigned to this warehouse yet.
                          </Text>
                        </Box>
                      ) : (
                        <VStack spacing={2} align="stretch">
                          {rows.map((row, index) => {
                            const driver = getDriverDisplay(row);
                            const availability = getDriverAvailability(driver);
                            const driverId = String(driver.id || "");
                            const removing =
                              busyDriverKey ===
                              `remove:${warehouseId}:${driverId}`;

                            return (
                              <Box
                                key={
                                  row?.assignment?.id ||
                                  `${warehouseId}:${driverId}:${index}`
                                }
                                p={3}
                                bg={theme.surfaceAlt}
                                border="1px solid"
                                borderColor={theme.border}
                                borderRadius={cardsBorderRadii}
                              >
                                <Flex
                                  justify="space-between"
                                  align={{ base: "flex-start", md: "center" }}
                                  direction={{ base: "column", md: "row" }}
                                  gap={3}
                                >
                                  <HStack minW={0} spacing={3} flex={1}>
                                    <Avatar size="sm" name={driver.name} />
                                    <Box minW={0}>
                                      <HStack spacing={2} flexWrap="wrap">
                                        <Text
                                          fontSize="sm"
                                          fontWeight="800"
                                          color={theme.text}
                                          noOfLines={1}
                                        >
                                          {driver.name}
                                        </Text>

                                        {driver.assignment.isPrimary ? (
                                          <Badge
                                            colorScheme="blue"
                                            borderRadius="5px"
                                          >
                                            Primary
                                          </Badge>
                                        ) : null}

                                        {driver.assignment.isBackup ? (
                                          <Badge
                                            colorScheme="purple"
                                            variant="subtle"
                                            borderRadius="5px"
                                          >
                                            Backup
                                          </Badge>
                                        ) : null}
                                      </HStack>

                                      <Text
                                        mt={0.5}
                                        fontSize="xs"
                                        color={theme.textMuted}
                                        noOfLines={1}
                                      >
                                        {driver.whatsappNumber ||
                                          driver.phoneNumber ||
                                          "No phone number"}
                                        {driver.vehicleType
                                          ? ` • ${driver.vehicleType.replace(/_/g, " ")}`
                                          : ""}
                                      </Text>
                                    </Box>
                                  </HStack>

                                  <HStack spacing={2} flexWrap="wrap">
                                    <Badge
                                      colorScheme={
                                        availability.online ? "green" : "gray"
                                      }
                                      borderRadius="5px"
                                    >
                                      {availability.online
                                        ? "Online"
                                        : "Offline"}
                                    </Badge>

                                    <Badge
                                      colorScheme={
                                        availability.eligible
                                          ? "green"
                                          : "orange"
                                      }
                                      variant="subtle"
                                      borderRadius="5px"
                                    >
                                      {availability.eligible
                                        ? "Eligible"
                                        : "Not eligible"}
                                    </Badge>

                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="red"
                                      leftIcon={<DeleteIcon />}
                                      onClick={() =>
                                        removeDriver(warehouse, row)
                                      }
                                      isLoading={removing}
                                      isDisabled={
                                        Boolean(busyDriverKey) && !removing
                                      }
                                      borderRadius={cardsBorderRadii}
                                    >
                                      Remove
                                    </Button>
                                  </HStack>
                                </Flex>

                                <SimpleGrid
                                  columns={{ base: 2, md: 4 }}
                                  spacing={2}
                                  mt={3}
                                >
                                  <DriverDetail
                                    label="Shift"
                                    value={
                                      driver.currentShiftStatus
                                        ? driver.currentShiftStatus.replace(
                                            /_/g,
                                            " ",
                                          )
                                        : "—"
                                    }
                                    theme={theme}
                                  />
                                  <DriverDetail
                                    label="Jobs"
                                    value={`${availability.currentJobs} / ${availability.maxConcurrentJobs}`}
                                    theme={theme}
                                  />
                                  <DriverDetail
                                    label="Services"
                                    value={
                                      driver.serviceCategories.length
                                        ? driver.serviceCategories
                                            .map((value) =>
                                              String(value).replace(/_/g, " "),
                                            )
                                            .join(", ")
                                        : "Delivery"
                                    }
                                    theme={theme}
                                  />
                                  <DriverDetail
                                    label="Status"
                                    value={driver.status || "—"}
                                    theme={theme}
                                  />
                                </SimpleGrid>

                                {!availability.eligible &&
                                driver.driverResolved ? (
                                  <Text
                                    mt={2}
                                    fontSize="xs"
                                    color={theme.textMuted}
                                  >
                                    {availability.reason}
                                  </Text>
                                ) : null}

                                {!driver.driverResolved ? (
                                  <Text mt={2} fontSize="xs" color="orange.500">
                                    Assignment exists, but this driver profile
                                    could not be loaded.
                                  </Text>
                                ) : null}
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                    </Box>
                  ) : null}
                </Box>
              </WarehouseCard>
            );
          })}
        </VStack>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={saving ? () => undefined : modal.onClose}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          mx={3}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
        >
          <ModalHeader color={theme.text}>
            {editingWarehouse ? "Edit Warehouse" : "Add Warehouse"}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color={theme.text} fontSize="sm">
                    Name
                  </FormLabel>
                  <Input
                    value={form.name}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="e.g. Limbe PickDengu"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color={theme.text} fontSize="sm">
                    Photo
                  </FormLabel>
                  <Input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    p={1}
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                    onChange={(event) =>
                      setPhotoFile(event.target.files?.[0] || null)
                    }
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel color={theme.text} fontSize="sm">
                  Description
                </FormLabel>
                <Textarea
                  value={form.description}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  placeholder="Short warehouse description"
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                  resize="vertical"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={theme.text} fontSize="sm">
                  Address
                </FormLabel>
                <Input
                  value={form.address}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      address: event.target.value,
                    }))
                  }
                  placeholder="Warehouse street / location"
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color={theme.text} fontSize="sm">
                    Latitude
                  </FormLabel>
                  <Input
                    type="number"
                    step="any"
                    value={form.lat}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        lat: event.target.value,
                      }))
                    }
                    placeholder="-15.7861"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={theme.text} fontSize="sm">
                    Longitude
                  </FormLabel>
                  <Input
                    type="number"
                    step="any"
                    value={form.lng}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        lng: event.target.value,
                      }))
                    }
                    placeholder="35.0058"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  />
                </FormControl>
              </SimpleGrid>

              <Divider borderColor={theme.divider || theme.border} />

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <ToggleRow
                  label="Active"
                  checked={form.isActive}
                  onChange={(checked) =>
                    setForm((current) => ({ ...current, isActive: checked }))
                  }
                  theme={theme}
                />
                <ToggleRow
                  label="Default dispatch"
                  checked={form.isDefaultDispatch}
                  onChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      isDefaultDispatch: checked,
                    }))
                  }
                  theme={theme}
                />
                <ToggleRow
                  label="Supports pickup"
                  checked={form.supportsPickup}
                  onChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      supportsPickup: checked,
                    }))
                  }
                  theme={theme}
                />
                <ToggleRow
                  label="Supports delivery"
                  checked={form.supportsDelivery}
                  onChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      supportsDelivery: checked,
                    }))
                  }
                  theme={theme}
                />
              </SimpleGrid>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              onClick={modal.onClose}
              isDisabled={saving}
              borderRadius={cardsBorderRadii}
            >
              Cancel
            </Button>
            <Button
              onClick={saveWarehouse}
              isLoading={saving}
              loadingText="Saving"
              borderRadius={cardsBorderRadii}
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            >
              {editingWarehouse ? "Save Changes" : "Add Warehouse"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const DriverPicker = ({
  drivers,
  assignedDriverIds,
  warehouseActive,
  busy,
  loading,
  theme,
  onAssign,
}: {
  drivers: any[];
  assignedDriverIds: Set<string>;
  warehouseActive: boolean;
  busy: boolean;
  loading: boolean;
  theme: any;
  onAssign: (driver: any) => void | Promise<void>;
}) => {
  const [search, setSearch] = useState("");

  const availableDrivers = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return drivers
      .filter((driver) => {
        const id = getDriverId(driver);
        if (!id || assignedDriverIds.has(id)) return false;
        if (!needle) return true;

        return [
          driver?.name,
          driver?.phoneNumber,
          driver?.whatsappNumber,
          driver?.vehicleType,
          id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle);
      })
      .sort((left, right) => {
        const leftAvailability = getDriverAvailability(left);
        const rightAvailability = getDriverAvailability(right);

        if (leftAvailability.eligible !== rightAvailability.eligible) {
          return leftAvailability.eligible ? -1 : 1;
        }
        if (leftAvailability.online !== rightAvailability.online) {
          return leftAvailability.online ? -1 : 1;
        }
        return String(left?.name || "").localeCompare(
          String(right?.name || ""),
        );
      });
  }, [drivers, assignedDriverIds, search]);

  return (
    <Menu placement="bottom-end" matchWidth closeOnSelect>
      <MenuButton
        as={Button}
        size="sm"
        rightIcon={<ChevronDownIcon />}
        isDisabled={!warehouseActive || busy || loading}
        isLoading={loading}
        loadingText="Loading"
        borderRadius={cardsBorderRadii}
        border="1px solid"
        borderColor={theme.border}
        bg={theme.surface}
        color={theme.text}
        _hover={{ bg: theme.hover }}
        _active={{ bg: theme.hover }}
      >
        Add Driver
      </MenuButton>

      <MenuList
        p={0}
        maxH="350px"
        overflowY="auto"
        borderRadius={cardsBorderRadii}
        border="1px solid"
        borderColor={theme.border}
        bg={theme.surface}
        boxShadow={theme.shadowMd || theme.shadowSm}
        zIndex={80}
      >
        <Box
          position="sticky"
          top={0}
          zIndex={2}
          p={2}
          bg={theme.surface}
          borderBottom="1px solid"
          borderColor={theme.border}
        >
          <Input
            size="sm"
            h="38px"
            value={search}
            placeholder="Search drivers..."
            bg={theme.inputBg}
            borderColor={theme.inputBorder}
            onChange={(event) => setSearch(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          />
        </Box>

        <Box p={1}>
          {availableDrivers.length === 0 ? (
            <Text p={3} fontSize="sm" color={theme.textMuted}>
              {drivers.length > 0 && drivers.length === assignedDriverIds.size
                ? "All drivers are already assigned."
                : "No matching drivers found."}
            </Text>
          ) : (
            availableDrivers.map((driver) => {
              const id = getDriverId(driver);
              const availability = getDriverAvailability(driver);

              return (
                <MenuItem
                  key={id}
                  px={3}
                  py={2.5}
                  mb={1}
                  borderRadius={cardsBorderRadii}
                  bg="transparent"
                  _hover={{ bg: theme.hover }}
                  _focus={{ bg: theme.hover }}
                  onClick={() => {
                    setSearch("");
                    void onAssign(driver);
                  }}
                >
                  <Flex w="100%" justify="space-between" align="start" gap={3}>
                    <Box minW={0} flex={1}>
                      <Text
                        fontSize="sm"
                        fontWeight="800"
                        color={theme.text}
                        noOfLines={1}
                      >
                        {driver?.name || "Unnamed driver"}
                      </Text>
                      <Text
                        mt={0.5}
                        fontSize="xs"
                        color={theme.textMuted}
                        noOfLines={1}
                      >
                        {driver?.whatsappNumber ||
                          driver?.phoneNumber ||
                          "No phone"}
                        {driver?.vehicleType
                          ? ` • ${String(driver.vehicleType).replace(/_/g, " ")}`
                          : ""}
                      </Text>
                    </Box>

                    <VStack spacing={1} align="flex-end" flexShrink={0}>
                      <Badge
                        colorScheme={availability.online ? "green" : "gray"}
                        borderRadius="5px"
                      >
                        {availability.online ? "Online" : "Offline"}
                      </Badge>
                      <Badge
                        colorScheme={availability.eligible ? "green" : "orange"}
                        variant="subtle"
                        borderRadius="5px"
                      >
                        {availability.eligible ? "Eligible" : "Not eligible"}
                      </Badge>
                    </VStack>
                  </Flex>
                </MenuItem>
              );
            })
          )}
        </Box>
      </MenuList>
    </Menu>
  );
};

const DriverDetail = ({
  label,
  value,
  theme,
}: {
  label: string;
  value: string;
  theme: any;
}) => (
  <Box minW={0}>
    <Text fontSize="10px" color={theme.textSubtle}>
      {label}
    </Text>
    <Text
      mt={0.5}
      fontSize="xs"
      fontWeight="600"
      color={theme.text}
      textTransform="capitalize"
      noOfLines={1}
    >
      {value}
    </Text>
  </Box>
);

const ToggleRow = ({
  label,
  checked,
  onChange,
  theme,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  theme: any;
}) => (
  <Flex
    px={3}
    py={2.5}
    border="1px solid"
    borderColor={theme.border}
    borderRadius={cardsBorderRadii}
    bg={theme.surfaceAlt}
    align="center"
    justify="space-between"
    gap={3}
  >
    <Text fontSize="sm" fontWeight="600" color={theme.text}>
      {label}
    </Text>
    <Switch
      isChecked={checked}
      onChange={(event) => onChange(event.target.checked)}
      colorScheme="green"
    />
  </Flex>
);

export default WarehouseManager;
